"use client";

import Image from "next/image";
import Link from "@/components/ui/Link";
import { MessageIcon } from "../ui/message-icon";
import { BOOKMARK_CATEGORIES } from "@/lib/api/public-client";
import { FALLBACK_IMAGE } from '@/lib/constants';
import { getImageUrl, getTimeAgo, getFocusPointStyle, ImageMetadata, BLUR_PLACEHOLDER } from '@/lib/componentutills';

type FieldConfig = {
  hidden?: boolean;
  value?: string;
  customClass?: string;
};

type Props = {
  category?: FieldConfig;
  title?: FieldConfig;
  excerpt?: FieldConfig;
  timeAgo?: FieldConfig;
  imageSrc?: FieldConfig;
  slug?: string;
  priority?: boolean;
  imageMetadata?: ImageMetadata;
};

export default function HorizontalBigImageNewsWhite({
  title = { hidden: false, value: "'கடின கதோரி அண்டகடஹம்' திரைப்பட விமர்சனம்: வெகு தொனலியில் இல்லாத கடந்த காலத்திலிருந்து உணர்ச்சியை", customClass: "" },
  excerpt = { hidden: false, value: "நாட்டின் நிலக்கரி உற்பத்தி 2023 ஏப்ரலில் 8.5 சதவீதம் அதிகரித்து 7.314 கோடி டன்னாக", customClass: "" },
  timeAgo = { hidden: false, value: "15 min ago", customClass: "" },
  imageSrc = { hidden: false, value: FALLBACK_IMAGE, customClass: "" },
  slug,
  priority = false,
  imageMetadata,
}: Props) {
  const titleValue = title?.value || "";
  const excerptValue = excerpt?.value || "";
  const imageSrcValue = getImageUrl(imageSrc?.value) || FALLBACK_IMAGE;
  const timeAgoValue = getTimeAgo(timeAgo?.value);

  const content = (
    <article className="bg-transparent overflow-hidden p-4 mb-0">
      <div className="flex flex-row gap-4">
        {/* LEFT: IMAGE */}
        {!imageSrc?.hidden && (
          <div className={`shrink-0 ${imageSrc?.customClass || ""}`}>
            <div className="relative w-[106px] h-[56px] md:w-[150px] md:h-[110px] lg:w-[180px] lg:h-[114px] overflow-hidden">
              <Image
                src={imageSrcValue}
                alt={titleValue}
                fill
                className="object-cover w-full h-full"
                style={getFocusPointStyle(imageMetadata)}
                sizes="(max-width: 768px) 100px, 300px"
                loading={priority ? "eager" : "lazy"}
                fetchPriority={priority ? "high" : "auto"}
                priority={priority}
                placeholder="blur"
                blurDataURL={BLUR_PLACEHOLDER}
              />
            </div>
          </div>
        )}

        {/* RIGHT: CONTENT */}
        <div className="flex flex-col justify-between w-full">
          {/* Top Content */}
          <div className="flex flex-col gap-3">
            {!title?.hidden && (
 <h3 className={`text-14-tamil-700 md:text-14-tamil-700 text-FFFFFF font-medium leading-[1.4] ${title?.customClass || ""}`}>
                {titleValue}
              </h3>
            )}

            {!excerpt?.hidden && (
              <p className={`text-11-tamil-400 md:text-12-tamil-400 text-FFFFFF leading-[1.5] line-clamp-2 ${excerpt?.customClass || ""}`}>
                {excerptValue}
              </p>
            )}
          </div>

          {/* Bottom Footer */}
          <div className="mt-3">
            <div className="flex items-center justify-between">
              {!timeAgo?.hidden && (
                <span className={`text-12-tamil-500 text-FFFFFF ${timeAgo?.customClass || ""}`}>{timeAgoValue}</span>
              )}
              <MessageIcon
                id="small-image-news-vert-message-icon"
                variant='message-icon-4'
                slug={slug}
                category={BOOKMARK_CATEGORIES.NEWS}
                title={titleValue}
                thumbnailUrl={imageSrcValue}
                description={excerptValue}
                articleUrl={slug ? `/${slug}` : undefined}
              />
            </div>
          </div>
        </div>
      </div>
    </article>
  );

  if (slug) {
    return (
      <Link href={`/${slug}`} className="block  transition-opacity cursor-pointer">
        {content}
      </Link>
    );
  }

  return content;
}
